import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, FileText, Download, AlertCircle, CheckCircle, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AdminGapAnalysisPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [analyses, setAnalyses] = useState([]);
  const [audits, setAudits] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  
  // Form
  const [selectedAuditId, setSelectedAuditId] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [primaryLocation, setPrimaryLocation] = useState('');
  const [services, setServices] = useState('');
  
  // GBP
  const [gbpClaimed, setGbpClaimed] = useState(false);
  const [gbpVerified, setGbpVerified] = useState(false);
  const [gbpPhotosCount, setGbpPhotosCount] = useState(0);
  const [gbpPostsPerMonth, setGbpPostsPerMonth] = useState(0);
  
  // Reviews
  const [googleReviewCount, setGoogleReviewCount] = useState(0);
  const [googleAvgRating, setGoogleAvgRating] = useState(0);
  const [reviewsRespondedPercent, setReviewsRespondedPercent] = useState(0);
  
  // Social
  const [hasYoutube, setHasYoutube] = useState(false);
  const [youtubeVideoCount, setYoutubeVideoCount] = useState(0);
  const [hasTiktok, setHasTiktok] = useState(false);
  const [hasInstagram, setHasInstagram] = useState(false);
  
  // Website
  const [hasBlog, setHasBlog] = useState(false);
  const [blogPostCount, setBlogPostCount] = useState(0);
  const [hasServicePages, setHasServicePages] = useState(false);
  const [servicePageCount, setServicePageCount] = useState(0);
  const [hasLocationPages, setHasLocationPages] = useState(false);
  const [locationPageCount, setLocationPageCount] = useState(0);
  
  useEffect(() => {
    const auth = sessionStorage.getItem('gap_admin_auth');
    if (auth === 'true') setIsAuthenticated(true);
  }, []);
  
  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);
  
  const fetchData = async () => {
    try {
      const [analysesRes, auditsRes] = await Promise.all([
        fetch(`${API_URL}/api/gap-analysis`),
        fetch(`${API_URL}/api/audits`)
      ]);
      if (analysesRes.ok) setAnalyses(await analysesRes.json());
      if (auditsRes.ok) setAudits(await auditsRes.json());
    } catch (err) {
      console.error(err);
    }
  };
  
  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/admin/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      if (res.ok) {
        setIsAuthenticated(true);
        sessionStorage.setItem('gap_admin_auth', 'true');
      } else {
        setAuthError('Invalid password');
      }
    } catch (err) {
      setAuthError('Auth failed');
    }
  };
  
  const handleAuditSelect = (auditId) => {
    setSelectedAuditId(auditId);
    const audit = audits.find(a => a.id === auditId);
    if (audit) {
      setBusinessName(audit.business_name || '');
      setPrimaryLocation(audit.location || '');
      setServices(audit.service_type || '');
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedAuditId) {
      alert('Please select an audit');
      return;
    }
    setAnalyzing(true);
    try {
      const res = await fetch(`${API_URL}/api/gap-analysis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          audit_id: selectedAuditId,
          business_name: businessName,
          website_url: websiteUrl,
          primary_location: primaryLocation,
          services: services.split(',').map(s => s.trim()).filter(Boolean),
          gbp_claimed: gbpClaimed,
          gbp_verified: gbpVerified,
          gbp_photos_count: parseInt(gbpPhotosCount) || 0,
          gbp_posts_per_month: parseInt(gbpPostsPerMonth) || 0,
          google_review_count: parseInt(googleReviewCount) || 0,
          google_avg_rating: parseFloat(googleAvgRating) || 0,
          reviews_responded_percent: parseInt(reviewsRespondedPercent) || 0,
          has_youtube: hasYoutube,
          youtube_video_count: parseInt(youtubeVideoCount) || 0,
          has_tiktok: hasTiktok,
          has_instagram: hasInstagram,
          has_blog: hasBlog,
          blog_post_count: parseInt(blogPostCount) || 0,
          has_service_pages: hasServicePages,
          service_page_count: parseInt(servicePageCount) || 0,
          has_location_pages: hasLocationPages,
          location_page_count: parseInt(locationPageCount) || 0
        })
      });
      if (res.ok) {
        const data = await res.json();
        fetchData();
        setShowForm(false);
        window.open(`/gap-analysis/${data.id}`, '_blank');
      } else {
        alert('Error running analysis');
      }
    } catch (err) {
      alert('Error');
    } finally {
      setAnalyzing(false);
    }
  };
  
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0a2540] flex items-center justify-center p-8">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-[#d4af37] rounded-xl flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-[#0a2540]" />
            </div>
            <CardTitle className="text-2xl">Gap Analysis Admin</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label>Password</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1" />
              </div>
              {authError && <p className="text-red-500 text-sm">{authError}</p>}
              <Button type="submit" className="w-full bg-[#0a2540]">Access</Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50 p-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0a2540]">Gap Analysis</h1>
            <p className="text-slate-600">Deep-dive visibility assessment</p>
          </div>
          <Button onClick={() => setShowForm(!showForm)} className="bg-[#d4af37] text-[#0a2540]">
            {showForm ? 'Cancel' : 'New Analysis'}
          </Button>
        </div>
        
        {showForm && (
          <Card className="mb-8">
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Linked Audit (Required)</Label>
                    <select value={selectedAuditId} onChange={(e) => handleAuditSelect(e.target.value)} className="w-full border rounded p-2 mt-1" required>
                      <option value="">Select audit...</option>
                      {audits.map((a) => <option key={a.id} value={a.id}>{a.business_name} - {a.visibility_score}%</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Business Name</Label>
                    <Input value={businessName} onChange={(e) => setBusinessName(e.target.value)} required className="mt-1" />
                  </div>
                  <div>
                    <Label>Location</Label>
                    <Input value={primaryLocation} onChange={(e) => setPrimaryLocation(e.target.value)} required className="mt-1" />
                  </div>
                  <div>
                    <Label>Website URL</Label>
                    <Input value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} required className="mt-1" placeholder="https://..." />
                  </div>
                  <div>
                    <Label>Services (comma separated)</Label>
                    <Input value={services} onChange={(e) => setServices(e.target.value)} className="mt-1" />
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Google Business Profile</h3>
                  <div className="grid grid-cols-4 gap-4">
                    <label className="flex items-center gap-2"><input type="checkbox" checked={gbpClaimed} onChange={(e) => setGbpClaimed(e.target.checked)} /> Claimed</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={gbpVerified} onChange={(e) => setGbpVerified(e.target.checked)} /> Verified</label>
                    <div><Label>Photos</Label><Input type="number" value={gbpPhotosCount} onChange={(e) => setGbpPhotosCount(e.target.value)} className="mt-1" /></div>
                    <div><Label>Posts/month</Label><Input type="number" value={gbpPostsPerMonth} onChange={(e) => setGbpPostsPerMonth(e.target.value)} className="mt-1" /></div>
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Reviews</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div><Label>Google Review Count</Label><Input type="number" value={googleReviewCount} onChange={(e) => setGoogleReviewCount(e.target.value)} className="mt-1" /></div>
                    <div><Label>Avg Rating</Label><Input type="number" step="0.1" value={googleAvgRating} onChange={(e) => setGoogleAvgRating(e.target.value)} className="mt-1" /></div>
                    <div><Label>% Responded</Label><Input type="number" value={reviewsRespondedPercent} onChange={(e) => setReviewsRespondedPercent(e.target.value)} className="mt-1" /></div>
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Social</h3>
                  <div className="grid grid-cols-4 gap-4">
                    <label className="flex items-center gap-2"><input type="checkbox" checked={hasYoutube} onChange={(e) => setHasYoutube(e.target.checked)} /> YouTube</label>
                    {hasYoutube && <div><Label>Videos</Label><Input type="number" value={youtubeVideoCount} onChange={(e) => setYoutubeVideoCount(e.target.value)} className="mt-1" /></div>}
                    <label className="flex items-center gap-2"><input type="checkbox" checked={hasTiktok} onChange={(e) => setHasTiktok(e.target.checked)} /> TikTok</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={hasInstagram} onChange={(e) => setHasInstagram(e.target.checked)} /> Instagram</label>
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Website Content</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="flex flex-col gap-2">
                      <label className="flex items-center gap-2"><input type="checkbox" checked={hasBlog} onChange={(e) => setHasBlog(e.target.checked)} /> Has Blog</label>
                      {hasBlog && <Input type="number" value={blogPostCount} onChange={(e) => setBlogPostCount(e.target.value)} placeholder="Post count" />}
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="flex items-center gap-2"><input type="checkbox" checked={hasServicePages} onChange={(e) => setHasServicePages(e.target.checked)} /> Service Pages</label>
                      {hasServicePages && <Input type="number" value={servicePageCount} onChange={(e) => setServicePageCount(e.target.value)} placeholder="Page count" />}
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="flex items-center gap-2"><input type="checkbox" checked={hasLocationPages} onChange={(e) => setHasLocationPages(e.target.checked)} /> Location Pages</label>
                      {hasLocationPages && <Input type="number" value={locationPageCount} onChange={(e) => setLocationPageCount(e.target.value)} placeholder="Page count" />}
                    </div>
                  </div>
                </div>
                
                <Button type="submit" className="w-full bg-[#0a2540]" disabled={analyzing}>
                  {analyzing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running Analysis...</> : 'Run Gap Analysis'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
        
        <div>
          <h2 className="text-xl font-semibold mb-4">Gap Analyses</h2>
          {analyses.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-slate-500">No analyses yet</CardContent></Card>
          ) : (
            <div className="space-y-4">
              {analyses.map((a) => (
                <Card key={a.id}>
                  <CardContent className="p-6 flex justify-between items-center">
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold">{a.business_name}</h3>
                        <span className={`px-3 py-1 rounded text-white text-sm ${a.overall_status === 'red' ? 'bg-red-500' : a.overall_status === 'yellow' ? 'bg-yellow-500' : 'bg-green-500'}`}>
                          {a.overall_score}/100
                        </span>
                      </div>
                      <p className="text-slate-500 text-sm">{a.primary_location} • {a.created_at?.slice(0,10)}</p>
                      <div className="mt-2 flex gap-2 text-sm">
                        <span className="bg-red-100 text-red-800 px-2 py-1 rounded">{a.critical_findings} Critical</span>
                        <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded">{a.warning_findings} Warnings</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => window.open(`/gap-analysis/${a.id}`, '_blank')}>
                        <FileText className="w-4 h-4 mr-1" /> View
                      </Button>
                      <Button variant="outline" size="sm" onClick={async () => {
                        const res = await fetch(`${API_URL}/api/gap-analysis/${a.id}/pdf`);
                        const data = await res.json();
                        const w = window.open('', '_blank');
                        w.document.write(data.html);
                        w.document.close();
                        w.onload = () => w.print();
                      }}>
                        <Download className="w-4 h-4 mr-1" /> PDF
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminGapAnalysisPage;
